import L from 'leaflet';
import { addGoogleFleetBasemap, getGoogleMapsApiKey } from './googleMaps';

/**
 * CARTO raster basemaps require a key since 25 Sep 2026.
 * Docs use `?key=` — https://docs.carto.com/faqs/carto-basemaps
 * Optional: VITE_CARTO_BASEMAPS_API_KEY
 *
 * When CARTO key is missing/invalid, use Esri World Street Map (no key).
 * Prefer Google Maps JS when VITE_GOOGLE_MAPS_API_KEY is set (same as Fleet).
 */
export function getLeafletRasterBasemapOptions(): {
  url: string;
  options: L.TileLayerOptions;
} {
  const cartoKey = (import.meta.env.VITE_CARTO_BASEMAPS_API_KEY ?? '').trim();
  if (cartoKey) {
    return {
      url: `https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png?key=${encodeURIComponent(cartoKey)}`,
      options: {
        maxZoom: 20,
        subdomains: 'abcd',
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>',
      },
    };
  }

  // Esri — no API key; note {z}/{y}/{x} order (not x/y).
  return {
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}',
    options: {
      maxZoom: 19,
      attribution:
        'Tiles &copy; <a href="https://www.esri.com/">Esri</a> &mdash; Source: Esri, OpenStreetMap',
    },
  };
}

export function addLeafletRasterBasemap(map: L.Map): L.TileLayer {
  const { url, options } = getLeafletRasterBasemapOptions();
  return L.tileLayer(url, options).addTo(map);
}

/** Google (if configured) → CARTO/Esri raster. */
export async function addPreferredLeafletBasemap(
  map: L.Map,
): Promise<'google' | 'raster'> {
  if (getGoogleMapsApiKey()) {
    const google = await addGoogleFleetBasemap(map);
    if (google.ok) return 'google';
  }
  addLeafletRasterBasemap(map);
  return 'raster';
}
